console.log("VPX COACH FILE LOADED");

(function () {

    if (!window.VORYNEX) {
        console.error("VORYNEX namespace not found. Coach module aborted.");
        return;
    }

    /*
    ==========================================
    Free "Smart Coach"
    ------------------------------------------
    NOT a live AI — a curated set of common
    questions, each answered with a template
    filled from the user's REAL calculated data.
    Zero external cost, always available. Free
    text input is matched to the closest question
    via keyword matching, and the reply language
    is auto-detected from what the user typed —
    not tied to the site's current UI language
    toggle. Still not true NLP, just substring
    matching + script/character detection, but
    feels much closer to "ask anything" while
    staying 100% free and backend-free. The real
    conversational, ask-anything AI Coach is a
    separate future module requiring a backend.
    ==========================================
    */

    const QUESTION_IDS = [
        "calories", "bmr", "confidence", "macros", "timeline",
        "restday", "rir", "splitchoice", "supplements", "water",
        "progressive_overload", "weighin_frequency", "hunger_tips",
        "change_goal", "tdee_explained", "sleep_importance",
        "cheat_meal", "plateau"
    ];

    function isTodayTrainingDayLocal() {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return true;
        const schedule = window.VORYNEX.state.getSection("trainingSchedule");
        if (!schedule || !schedule.days || schedule.days.length === 0) return true;
        return schedule.days.indexOf(new Date().getDay()) !== -1;
    }

    function getOnboardingAnswers() {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return null;
        const section = window.VORYNEX.state.getSection("onboarding");
        return (section && section.answers) ? section.answers : null;
    }

    function buildVars(questionId, data) {

        switch (questionId) {

            case "calories": {
                const tdee = data.meta ? data.meta.tdee : data.targets.calories;
                const diff = Math.abs(tdee - data.targets.calories);
                const directionKey = data.targets.calories < tdee ? "coach_word_deficit" : (data.targets.calories > tdee ? "coach_word_surplus" : "coach_word_same");
                return { calories: data.targets.calories, tdee: tdee, diff: diff, directionKey: directionKey };
            }

            case "bmr": {
                return {
                    bmr: data.meta ? data.meta.bmr : "—",
                    equation: data.meta ? data.meta.bmrEquationUsed : "—"
                };
            }

            case "confidence": {
                const conf = data.meta ? data.meta.confidence : { score: 0, checklist: [] };
                const missing = (conf.checklist || []).filter(function (item) { return !item.present; });
                return { score: conf.score, missingCount: missing.length };
            }

            case "macros": {
                return {
                    protein: data.targets.protein,
                    carbs: data.targets.carbs,
                    fat: data.targets.fat
                };
            }

            case "timeline": {
                return {
                    current: data.goal.currentWeight,
                    target: data.goal.targetWeight,
                    weeks: data.goal.weeksRemaining,
                    rate: data.goal.weeklyRateKg || 0
                };
            }

            case "restday": {
                return { isTrainingToday: isTodayTrainingDayLocal() };
            }

            case "splitchoice": {
                if (!data.workoutPlan) return { hasPlan: false };
                const hasSuperset = data.workoutPlan.days.some(function (d) {
                    return d.exercises.some(function (ex) { return !!ex.supersetGroup; });
                });
                return {
                    hasPlan: true,
                    splitNameKey: data.workoutPlan.splitNameKey,
                    dayCount: data.workoutPlan.days.length,
                    hasSuperset: hasSuperset
                };
            }

            case "supplements": {
                const recs = data.supplementRecommendations || [];
                return { count: recs.length };
            }

            case "water": {
                return { water: data.targets.water, weight: data.goal.currentWeight };
            }

            case "tdee_explained": {
                return { tdee: data.meta ? data.meta.tdee : data.targets.calories };
            }

            case "sleep_importance": {
                const answers = getOnboardingAnswers();
                return { sleepHours: answers ? answers.sleepHours : "—" };
            }

            case "rir":
            case "progressive_overload":
            case "weighin_frequency":
            case "hunger_tips":
            case "change_goal":
            case "cheat_meal":
            case "plateau":
                return {};

            default:
                return {};
        }
    }

    /*
    ==========================================
    Language Detection
    ------------------------------------------
    Greek is detected reliably via Unicode range
    (Greek script is unmistakable). German is
    detected via umlauts/ß as a heuristic — not
    perfect (German without umlauts falls back to
    English), but a reasonable approximation
    without needing a real language-detection
    library.
    ==========================================
    */

    function detectLanguage(text) {
        if (/[\u0370-\u03FF\u1F00-\u1FFF]/.test(text)) return "el";
        if (/[äöüßÄÖÜ]/.test(text)) return "de";
        if (/\b(und|welches|passt|wie|was|mein|meine|kann|ich|nicht|brauche)\b/i.test(text)) return "de";
        return "en";
    }

    // Strips Greek tone marks (and other combining diacritics) so
    // "ταιριάζει" and "ταιριαζει" match identically — without this,
    // real Greek input (which almost always has accents) would fail
    // to match accent-free keyword lists.
    function normalizeForMatch(text) {
        return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    }

    /*
    ==========================================
    Free-Text Matching
    ------------------------------------------
    Simple keyword scoring, per language, so
    typing a question in EL/EN/DE finds the
    closest pre-written answer. Not real NLP —
    just substring matching — but gives a
    "type anything" feel without any backend.
    Expanded with common synonyms/phrasings.
    ==========================================
    */

    const KEYWORDS = {

        el: {
            calories: ["θερμιδ", "kcal"],
            bmr: ["bmr", "μεταβολισμ", "μεταβολικο"],
            confidence: ["confidence", "σκορ", "ακριβει", "πληροτητ"],
            macros: ["macro", "μακρο", "πρωτεϊν", "πρωτειν", "υδατανθρακ", "λιπο"],
            timeline: ["ποτε", "ποσο καιρο", "χρονοδιαγραμμα", "χρονος", "εβδομαδ"],
            restday: ["αλλαξε", "ρεστ", "ξεκουρασ"],
            rir: ["rir", "επαναληψ", "εξαντλησ"],
            splitchoice: ["προγραμμα προπονησ", "γυμναστικ", "split", "ppl", "push pull", "ταιριαζ", "ασκησ"],
            supplements: ["συμπληρωμα", "βιταμιν"],
            water: ["νερο", "ενυδατωσ"],
            progressive_overload: ["υπερφορτωσ", "προοδο", "αυξηση βαρους"],
            weighin_frequency: ["ζυγιζ", "ζυγαρια", "ζυγισμα", "συχνα"],
            hunger_tips: ["πειναω", "πεινα", "πεινασμεν"],
            change_goal: ["αλλαξω στοχο", "restart", "ξαναρχισ"],
            tdee_explained: ["tdee"],
            sleep_importance: ["υπνο", "κοιμ"],
            cheat_meal: ["cheat", "απατη γευμα", "εξω φαγ"],
            plateau: ["δεν χανω", "σταματησε", "plateau", "κολλησα"]
        },

        en: {
            calories: ["calor", "kcal"],
            bmr: ["bmr", "metabolis"],
            confidence: ["confidence", "score", "accura"],
            macros: ["macro", "protein", "carb", "fat"],
            timeline: ["when", "how long", "timeline", "how many weeks"],
            restday: ["changed", "rest day", "why did my meal"],
            rir: ["rir", "reps in reserve", "to failure"],
            splitchoice: ["training program", "workout program", "split", "ppl", "push pull", "which workout", "what workout", "suits me", "fits me", "gym program"],
            supplements: ["supplement", "vitamin", "what should i take"],
            water: ["water", "hydrat", "how much water"],
            progressive_overload: ["progressive overload", "add weight", "increase weight"],
            weighin_frequency: ["weigh", "scale", "how often"],
            hunger_tips: ["hungry", "hunger"],
            change_goal: ["change goal", "restart", "new quiz"],
            tdee_explained: ["tdee", "what is tdee"],
            sleep_importance: ["sleep"],
            cheat_meal: ["cheat", "eat out"],
            plateau: ["plateau", "not losing", "stuck", "no change"]
        },

        de: {
            calories: ["kalor", "kcal"],
            bmr: ["bmr", "stoffwechsel", "grundumsatz"],
            confidence: ["confidence", "score", "genauigkeit"],
            macros: ["makro", "protein", "kohlenhydrat", "fett"],
            timeline: ["wann", "wie lange", "zeitplan", "wie viele wochen"],
            restday: ["geändert", "ruhetag"],
            rir: ["rir", "wiederholungen", "bis zum versagen"],
            splitchoice: ["trainingsprogramm", "split", "ppl", "welches training", "passt zu mir", "fitnessprogramm"],
            supplements: ["supplement", "nahrungsergänzung", "vitamin", "was sollte ich nehmen"],
            water: ["wasser", "hydrat", "wie viel wasser"],
            progressive_overload: ["progressive overload", "gewicht steigern"],
            weighin_frequency: ["wiegen", "waage", "wie oft"],
            hunger_tips: ["hunger", "hungrig"],
            change_goal: ["ziel ändern", "neu starten", "neues quiz"],
            tdee_explained: ["tdee", "was ist tdee"],
            sleep_importance: ["schlaf"],
            cheat_meal: ["cheat", "auswärts essen"],
            plateau: ["plateau", "keine veränderung", "stagniert"]
        }
    };

    function matchQuestion(inputText, lang) {

        const useLang = lang || ((window.VORYNEX.i18n && window.VORYNEX.i18n.getLang) ? window.VORYNEX.i18n.getLang() : "en");
        const dict = KEYWORDS[useLang] || KEYWORDS.en;
        const normalized = normalizeForMatch(inputText);

        let bestId = null;
        let bestScore = 0;

        Object.keys(dict).forEach(function (id) {
            let score = 0;
            dict[id].forEach(function (kw) {
                if (normalized.indexOf(normalizeForMatch(kw)) !== -1) score++;
            });
            if (score > bestScore) {
                bestScore = score;
                bestId = id;
            }
        });

        return bestScore > 0 ? bestId : null;
    }

    /*
    ==========================================
    Public API
    ==========================================
    */

    const Coach = {};

    Coach.getQuestionIds = function () {
        return QUESTION_IDS.slice();
    };

    Coach.getAnswerVars = function (questionId, data) {
        return buildVars(questionId, data);
    };

    Coach.matchQuestion = function (inputText, lang) {
        return matchQuestion(inputText, lang);
    };

    Coach.detectLanguage = function (text) {
        return detectLanguage(text);
    };

    window.VORYNEX.coach = Coach;
    console.log("Coach module exported.");

})();